/**
 * Listener Time-Based Bonus — pure types and helpers for the page, kept out of
 * the component so the rules are unit-testable (the welcome-rules precedent).
 *
 * The API stores the whole config as one JSON SystemSetting and validates every
 * write as a unit; the checks here only catch problems before the round trip.
 * Times travel as minutes after local midnight; `endMinute <= startMinute`
 * means the period crosses midnight (Night Bonus 21:00 → 03:00). Windows are
 * half-open [start, end): the shared minute of two adjacent periods belongs to
 * the later one. Money travels as integer PAISE; the form edits rupees.
 */

/** How declined / unanswered rings pause a period's accrual. */
export interface PenaltyPolicy {
  /** Misses ALLOWED per evaluation window; one more starts a pause (0–100). */
  maxMisses: number;
  /** Rolling window the misses are counted in, minutes (1–1440). */
  evalWindowMinutes: number;
  /** How accrual comes back: after a cooldown, or not before the window ends. */
  recovery: 'cooldown' | 'window_end';
  /** Pause length for recovery `cooldown`, minutes (1–1440). */
  cooldownMinutes: number;
}

/** The API's default: a second qualifying miss pauses accrual for 30 min. */
export const DEFAULT_PENALTY: PenaltyPolicy = {
  maxMisses: 1,
  evalWindowMinutes: 60,
  recovery: 'cooldown',
  cooldownMinutes: 30,
};

/** Which online-session kind qualifies as "available". */
export type AvailabilityBasis = 'callable' | 'screen_on';

export const AVAILABILITY_BASIS_OPTIONS: Array<{ value: AvailabilityBasis; label: string }> = [
  { value: 'callable', label: 'Callable (ringable presence — locked phone on push lease counts)' },
  { value: 'screen_on', label: 'Screen on (stricter live heartbeat, like online-earning stages)' },
];

export interface TimeBonusPeriod {
  id: string;
  name: string;
  startMinute: number;
  endMinute: number;
  rewardPaise: number;
  targetMinutes: number;
  penalty: PenaltyPolicy;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TimeBonusConfig {
  enabled: boolean;
  timezone: string;
  carryForward: 'none';
  availabilityBasis: AvailabilityBasis;
  periods: TimeBonusPeriod[];
}

export interface BonusRewardRow {
  id: string;
  periodId: string;
  periodName: string;
  seq: number;
  rewardPaise: number;
  targetMinutes: number;
  windowStart: string;
  windowEnd: string;
  dateKey: string;
  status: 'pending' | 'credited';
  transactionId: string | null;
  createdAt: string;
  creditedAt: string | null;
  listener: { id: string; displayName: string | null; handle: string | null };
}

export interface PeriodForm {
  name: string;
  /** "HH:mm" for the time inputs. */
  startTime: string;
  endTime: string;
  /** Rupees as typed (the body converts to paise). */
  rewardRupees: number | null;
  targetMinutes: number | null;
  /** Decline rule — see PenaltyPolicy. Nulls while the input is cleared. */
  maxMisses: number | null;
  evalWindowMinutes: number | null;
  recovery: PenaltyPolicy['recovery'];
  cooldownMinutes: number | null;
  enabled: boolean;
}

export const EMPTY_PERIOD_FORM: PeriodForm = {
  name: '',
  startTime: '21:00',
  endTime: '03:00',
  rewardRupees: null,
  targetMinutes: 60,
  maxMisses: DEFAULT_PENALTY.maxMisses,
  evalWindowMinutes: DEFAULT_PENALTY.evalWindowMinutes,
  recovery: DEFAULT_PENALTY.recovery,
  cooldownMinutes: DEFAULT_PENALTY.cooldownMinutes,
  enabled: true,
};

export const NAME_MAX = 60;
export const MAX_REWARD_RUPEES = 10_000;

/** 480 → "08:00". */
export function minuteToTime(minute: number): string {
  const h = Math.floor(minute / 60);
  return `${String(h).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
}

/** "08:00" → 480; null when it is not a time. */
export function timeToMinute(time: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h > 23 || min > 59 ? null : h * 60 + min;
}

/** 1260 → "9:00 PM" — how the table shows a period boundary. */
export function minuteTo12h(minute: number): string {
  const h = Math.floor(minute / 60);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(minute % 60).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

/** 2000 → "₹20", 2050 → "₹20.50". */
export function paiseToRupees(paise: number): string {
  return paise % 100 === 0 ? `₹${paise / 100}` : `₹${(paise / 100).toFixed(2)}`;
}

export function formFromPeriod(p: TimeBonusPeriod): PeriodForm {
  const penalty = p.penalty ?? DEFAULT_PENALTY;
  return {
    name: p.name,
    startTime: minuteToTime(p.startMinute),
    endTime: minuteToTime(p.endMinute),
    rewardRupees: p.rewardPaise / 100,
    targetMinutes: p.targetMinutes,
    maxMisses: penalty.maxMisses,
    evalWindowMinutes: penalty.evalWindowMinutes,
    recovery: penalty.recovery,
    cooldownMinutes: penalty.cooldownMinutes,
    enabled: p.enabled,
  };
}

/** A period's minute coverage as linear segments on [0, 1440). */
function minuteSegments(startMinute: number, endMinute: number): Array<[number, number]> {
  if (startMinute < endMinute) return [[startMinute, endMinute]];
  const segs: Array<[number, number]> = [[startMinute, 1440]];
  if (endMinute > 0) segs.push([0, endMinute]);
  return segs;
}

/** Whether two periods share any minute of the 24 h clock. */
export function periodsOverlap(
  a: { startMinute: number; endMinute: number },
  b: { startMinute: number; endMinute: number },
): boolean {
  const sa = minuteSegments(a.startMinute, a.endMinute);
  const sb = minuteSegments(b.startMinute, b.endMinute);
  return sa.some(([s1, e1]) => sb.some(([s2, e2]) => s1 < e2 && s2 < e1));
}

/**
 * The first problem with the form, or null when it can be saved. `others` are
 * the OTHER periods (the one being edited excluded) for the overlap check —
 * only enabled ones can clash, and only when this form is enabled too.
 */
export function periodError(
  form: PeriodForm,
  others: Array<Pick<TimeBonusPeriod, 'name' | 'startMinute' | 'endMinute' | 'enabled'>>,
): string | null {
  const name = form.name.trim();
  if (name.length === 0) return 'Give the period a name.';
  if (name.length > NAME_MAX) return `Keep the name under ${NAME_MAX} characters.`;
  const start = timeToMinute(form.startTime);
  const end = timeToMinute(form.endTime);
  if (start === null) return 'Start time must be a valid time (HH:mm).';
  if (end === null) return 'End time must be a valid time (HH:mm).';
  if (start === end) return 'Start and end must differ — a 24-hour period is not supported.';
  if (
    form.rewardRupees === null ||
    !Number.isFinite(form.rewardRupees) ||
    form.rewardRupees <= 0 ||
    form.rewardRupees > MAX_REWARD_RUPEES
  ) {
    return `Reward must be between ₹0.01 and ₹${MAX_REWARD_RUPEES.toLocaleString('en-IN')}.`;
  }
  if (Math.round(form.rewardRupees * 100) !== form.rewardRupees * 100) {
    return 'Reward cannot be finer than a paisa.';
  }
  if (
    form.targetMinutes === null ||
    !Number.isInteger(form.targetMinutes) ||
    form.targetMinutes < 1 ||
    form.targetMinutes > 1440
  ) {
    return 'Qualifying online minutes must be a whole number from 1 to 1440.';
  }
  if (form.maxMisses === null || !Number.isInteger(form.maxMisses) || form.maxMisses < 0 || form.maxMisses > 100) {
    return 'Allowed misses must be a whole number from 0 to 100.';
  }
  if (
    form.evalWindowMinutes === null ||
    !Number.isInteger(form.evalWindowMinutes) ||
    form.evalWindowMinutes < 1 ||
    form.evalWindowMinutes > 1440
  ) {
    return 'The miss evaluation window must be 1 to 1440 minutes.';
  }
  // Under `window_end` the cooldown is unused (the body falls back to the
  // default, which the API requires to be a valid number anyway).
  if (
    form.recovery === 'cooldown' &&
    (form.cooldownMinutes === null ||
      !Number.isInteger(form.cooldownMinutes) ||
      form.cooldownMinutes < 1 ||
      form.cooldownMinutes > 1440)
  ) {
    return 'The cooldown must be 1 to 1440 minutes.';
  }
  if (form.enabled) {
    const me = { startMinute: start, endMinute: end };
    const clash = others.find((o) => o.enabled && periodsOverlap(me, o));
    if (clash) {
      return `This period overlaps "${clash.name}" — the same minute of availability must never qualify twice. Adjacent times are fine.`;
    }
  }
  return null;
}

/** Request body for create / update — exactly the fields the API accepts. */
export function periodBody(form: PeriodForm) {
  return {
    name: form.name.trim(),
    startMinute: timeToMinute(form.startTime) ?? 0,
    endMinute: timeToMinute(form.endTime) ?? 0,
    rewardPaise: Math.round((form.rewardRupees ?? 0) * 100),
    targetMinutes: form.targetMinutes ?? 60,
    penalty: {
      maxMisses: form.maxMisses ?? DEFAULT_PENALTY.maxMisses,
      evalWindowMinutes: form.evalWindowMinutes ?? DEFAULT_PENALTY.evalWindowMinutes,
      recovery: form.recovery,
      cooldownMinutes: form.cooldownMinutes ?? DEFAULT_PENALTY.cooldownMinutes,
    },
    enabled: form.enabled,
  };
}

/**
 * "Pauses after 2 declines/missed rings in 60 min · resumes after 30 min" —
 * one line for the periods table and the modal.
 */
export function declineRuleLabel(p: PenaltyPolicy | undefined): string {
  const pol = p ?? DEFAULT_PENALTY;
  const resume =
    pol.recovery === 'cooldown' ? `resumes after ${pol.cooldownMinutes} min` : 'paused until the window ends';
  return `Pauses after ${pol.maxMisses + 1} declines/missed rings in ${pol.evalWindowMinutes} min · ${resume}`;
}

/** Zones offered in the picker; the stored zone is always included. */
export function timezoneOptions(current?: string): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  const all = intl.supportedValuesOf?.('timeZone') ?? ['Asia/Kolkata', 'UTC'];
  return current && !all.includes(current) ? [current, ...all] : all;
}

/** "9:00 PM → 3:00 AM (overnight)" — one line for the table. */
export function periodWindowLabel(p: Pick<TimeBonusPeriod, 'startMinute' | 'endMinute'>): string {
  const overnight = p.endMinute <= p.startMinute;
  return `${minuteTo12h(p.startMinute)} → ${minuteTo12h(p.endMinute)}${overnight ? ' (overnight)' : ''}`;
}
